import { useEffect, useState } from 'react'
import './ReactVideoList.scss'

import { Modal, ModalTypes } from '@cox/core-ui8/dist/Modal'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import ReactVideoListSkeleton from './ReactVideoListSkeleton'
import { ReactVideoListProps, VideoItem, VideoListType } from './types'
import { CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON, CAROUSEL_INACTIVE_ARROW_BUTTON, YOUTUBE_ICON } from '../../assets/images'
import { getVideoPlaylist } from '../../services'
import { getImageSrc } from '../../utils/local-util'
import { getConditionResult } from '../../utils/rule-builder-util'
import { getSpacing } from '../../utils/spacing-util'
import { RuleType } from '../../utils/types'
import { getVideoThumbnail } from '../../utils/video-util'
import { ActionButtonTypes, responsiveCarouselProps } from '../ReactCarousel'
import ActionButton from '../ReactCarousel/ActionButton'
import CustomDot from '../ReactCarousel/CustomDot'
import ReactMultiCarouselV2 from '../ReactCarousel/ReactMultiCarouselV2'
import ReactImage from '../ReactImage'
import ReactText from '../ReactText'
import ReactVideo from '../ReactVideo/ReactVideo'
import { LayoutType } from '../ReactVideo/types'
import IsInEditorText from '../shared/IsInEditorText'
import ReactImageOverlay from '../shared/ReactImageOverlay'

const ReactVideoList = (props: ReactVideoListProps): JSX.Element => {
  const {
    eyebrow = '',
    displaytype,
    ruleExpression = '',
    videoProductList = [],
    maxVideos = 10,
    videoIcon = YOUTUBE_ICON,
    videoAltTxt = '',
    hidedescription = false,
    playlistview
  } = props
  const flexOffers = useFlexOffers(ruleExpression)

  const [videoList, setVideoList] = useState<VideoItem[]>()
  const [carouselItems, setCarouselItems] = useState<any[]>()
  const [loading, setLoading] = useState(false)
  const [modalVideoId, setModalVideoId] = useState('')

  useEffect(() => {
    getInitialVideoList()
  }, [])

  const getInitialVideoList = async () => {
    setLoading(true)
    const placeHolders = [
      <ReactVideoListSkeleton overrideHeight={playlistview === VideoListType.GRID} />,
      <ReactVideoListSkeleton overrideHeight={playlistview === VideoListType.GRID} />
    ]
    if (playlistview === VideoListType.GRID) {
      placeHolders.push(
        ...[
          <ReactVideoListSkeleton overrideHeight={playlistview === VideoListType.GRID} />,
          <ReactVideoListSkeleton overrideHeight={playlistview === VideoListType.GRID} />
        ]
      )
    }
    setCarouselItems(placeHolders)
    const response = await getVideoPlaylist(videoProductList, maxVideos)

    setLoading(false)
    if (response) {
      setVideoList(response)
    }
    const selectedVideos = getListOfVideos(videoProductList[0]?.productcategory, response)

    setCarouselItems(selectedVideos?.length > 0 ? getCarouselItems(selectedVideos) : [])
  }

  const handleVideoSelection = (videoId: string) => {
    setModalVideoId(videoId)
  }

  const getCarouselItems = (videosByCategory: VideoItem[]) => {
    const maxVideoLength = playlistview === VideoListType.GRID ? videosByCategory?.length : maxVideos
    const carouselItems = videosByCategory?.slice(0, maxVideoLength)?.map((item: any) => {
      return (
        <ReactVideoSlide
          key={`${item.id}_videolist`}
          videoId={item?.snippet?.resourceId?.videoId}
          index={0}
          videoIcon={videoIcon}
          videoAltTxt={videoAltTxt}
          heading={item?.snippet?.title}
          description={item?.snippet?.description}
          handleVideoSelection={handleVideoSelection}
          hidedescription={hidedescription}
          playlistview={playlistview}
        />
      )
    })

    //filters out any empty values
    return carouselItems.filter((elm: any) => elm)
  }

  const handleCategoryChange = (category: string): void => {
    // this has be named as All from aem? This shall be one exception?
    if (category === 'All') {
      if (videoList) setCarouselItems(getCarouselItems(videoList))
      return
    }
    if (videoList) setCarouselItems(getCarouselItems(getListOfVideos(category, videoList)))
  }

  const getListOfVideos = (category: string, list: VideoItem[]): VideoItem[] => {
    const video = videoProductList.find((product: any) => product.productcategory === category)

    if (video) {
      const videoResponse = list?.filter((playlist: any) => playlist.snippet.playlistId == video.playlistid)
      return videoResponse
    }
    return []
  }

  const { headline, description, activePip, inActivePip, activeAuthoredIndex, isInEditor = false } = props

  return (
    <>
      {IsInEditorText(isInEditor, 'React Video List')}
      <div
        className={`react-video-list-container ${getSpacing(
          true,
          true,
          true,
          true
        )} react-carousel responsive-size' data-testid='react-carousel-video-list`}
      >
        {Array.isArray(carouselItems) && (
          <ReactMultiCarouselV2
            //custom props
            title={headline}
            eyebrow={eyebrow}
            displaytype={displaytype}
            description={description}
            //multicarousel props
            ssr={true}
            partialVisible={true}
            responsive={responsiveCarouselProps}
            arrows={false}
            renderButtonGroupOutside={carouselItems?.length > 0}
            customButtonGroup={<ButtonGroup nextButton={props.nextButton} previousButton={props.previousButton} />}
            showDots={playlistview === VideoListType.CAROUSEL}
            renderDotsOutside={playlistview === VideoListType.CAROUSEL}
            swipeable={playlistview === VideoListType.CAROUSEL}
            customDot={<CustomDot activePip={activePip} inActivePip={inActivePip} />}
            flexOffers={flexOffers}
            activeAuthoredIndex={activeAuthoredIndex}
            isVideoList={true}
            handleCategoryChange={handleCategoryChange}
            categoryList={videoProductList.map((videoProduct: any) => videoProduct.productcategory)}
            loading={loading}
            playlistview={playlistview}
            isInEditor={isInEditor}
          >
            {carouselItems?.length > 0 ? (
              carouselItems?.map((carouselItem: any) => {
                return (
                  <div className={`${playlistview}-item-wrapper`} key={JSON.stringify(carouselItem.key)}>
                    {carouselItem}
                  </div>
                )
              })
            ) : (
              <></>
            )}
          </ReactMultiCarouselV2>
        )}
        {carouselItems?.length === 0 && videoProductList.length > 0 && (
          <div className='video-list-no-data'>No data available for the selected category</div>
        )}
      </div>
      <Modal
        show={modalVideoId != ''}
        modalId={modalVideoId}
        componentName={'video-list'}
        title={' '} //keep this as space for the close icon to align at the right.
        handleClose={() => setModalVideoId('')}
        modalType={ModalTypes.video}
        showFooter={true}
        primaryBtnText='Close'
        primaryBtnClick={() => setModalVideoId('')}
      >
        <ReactVideo youtubeVideoId={modalVideoId} isInEditor={false} layout={LayoutType.RESPONSIVE} autoplay={true} />
      </Modal>
    </>
  )
}

const ReactVideoSlide = (props: any) => {
  const {
    heading,
    description,
    ruleExpression = '',
    condition,
    ruleType,
    videoId,
    videoIcon,
    videoAltTxt = '',
    handleVideoSelection,
    hidedescription,
    playlistview
  } = props

  // flex offers success
  let conditionResult = true
  const flexOffers = useFlexOffers(ruleExpression)
  if (flexOffers?.success && ruleType === RuleType.conditional && condition) {
    conditionResult = getConditionResult(condition, flexOffers)
  }
  if (conditionResult) {
    const openThumbnail = (videoId: string) => {
      handleVideoSelection(videoId)
      // if (videoId) {
      //   //this is to handle the scroll back to the top on the click of the modal.
      //   //with this it will get back to the position of navigation buttons on smooth scroll.
      //   const videoContainer = document.querySelector(`.video-list-button-group`)
      //   setTimeout(function () {
      //     videoContainer?.scrollIntoView({ behavior: 'auto' })
      //   }, 50)
      // }
    }

    return (
      <div className='article react-video-list' onClick={() => openThumbnail(videoId)}>
        <div className='article-container responsive-size' data-testid='video-list-container'>
          <ReactImageOverlay
            additionalClass={`video-list-button-group`}
            overlay={{ src: getImageSrc(videoIcon) }}
            hoverOverlay={{ color: '#455051B2' }}
            alt={videoAltTxt}
          >
            <ReactImage src={getVideoThumbnail(videoId, 'mqdefault.jpg')} alt='Play' imgCss='image' />
          </ReactImageOverlay>
          {heading && <ReactText text={heading} isParsed className='heading' />}
          {playlistview === VideoListType.CAROUSEL && !hidedescription && description && (
            <ReactText text={description} className='headingDescription' />
          )}
        </div>
      </div>
    )
  }
  return <></>
}

const ButtonGroup = (props: any) => {
  const {
    carouselState: { currentSlide, totalItems },
    next,
    previous,
    nextButton = CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON,
    previousButton = CAROUSEL_INACTIVE_ARROW_BUTTON,
    nextButtonAltText = 'Next',
    previousButtonAltText = 'Previous'
  } = props
  return (
    <div className='carousel-button-group video-list-button-group'>
      <ActionButton
        buttonType={ActionButtonTypes.PREVIOUS}
        isDisabled={currentSlide === 0}
        type='button'
        onClick={() => previous()}
        aria-label={previousButtonAltText}
        aria-disabled={currentSlide === 0}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
      />
      <ActionButton
        buttonType={ActionButtonTypes.NEXT}
        isDisabled={currentSlide === totalItems - 2}
        type='button'
        onClick={() => next()}
        aria-label={nextButtonAltText}
        aria-disabled={currentSlide === totalItems - 2}
        //authorable icons
        carouselActiveButtonAltText={nextButtonAltText}
        carouselDisabledButtonAltText={previousButtonAltText}
        nextButton={nextButton}
        previousButton={previousButton}
      />
    </div>
  )
}
export default ReactVideoList
